import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import dayjs from 'dayjs';
import type { IMetricBarCardProps } from './_type';
import { getName } from '@home/utils/common';
import { formatMetricValue } from './_utils';
import { stringToFunction } from '../../utils/stringToJson';
import { LANGUAGE_EN } from '@home/_config/config';
import SvgIcon from '@home/components/SvgIcon';
import CardProgressBar from '../CardProgressBar';
import './index.scss';

/**
 * MetricBarCard 漏斗图组件
 */
const MetricBarCard: React.FC<IMetricBarCardProps> = (props) => {
  const {
    className,
    style,
    icon,
    title,
    titleEn,
    titleCn,
    time: propsTime,
    timeFormat = 'HH : mm',
    showTime = true,
    bars = [],
    defaultColors,
    children,
    componentMetrics = [],
    data,
    dataKey,
    formatData,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const metricCardData = useMemo(() => {
    const metricData = data?.[dataKey]?.data || [];
    let metricCardData = metricData;
    if (formatData) {
      if (typeof formatData === 'string') {
        metricCardData = stringToFunction(formatData)(metricData, data);
      }
      if (typeof formatData === 'function') {
        metricCardData = formatData(metricData, data);
      }
    }
    return Array.isArray(metricCardData) ? metricCardData : [];
  }, [data, dataKey, formatData]);

  // 格式化时间
  const formattedTime = useMemo(() => {
    const time = propsTime || metricCardData?.find((item: { metrics_name: string; }) => item?.metrics_name === 'time')?.value || "";
    if (!showTime || !time) return null;

    if (typeof time === 'string' || typeof time === 'number') {
      return dayjs(time).format(timeFormat);
    }

    if (time instanceof Date) {
      return dayjs(time).format(timeFormat);
    }

    return null;
  }, [propsTime, metricCardData, showTime, timeFormat]);

  // 默认颜色配置（如果未提供）
  const defaultBarColors = defaultColors || [
    ['#B4CDFF', '#5086F3'], // 蓝色渐变
    ['#D0EDDE', '#40B77A'], // 浅蓝渐变
    ['#D8D4F9', '#7F6FF1'], // 绿色渐变
    ['#DCF8F6', '#488E89'], // 黄色渐变
  ];

  // 使用最长值的指标值长度作为最小宽度，保证进度条长度一致，避免数值过长导致进度条长度不一致
  const minWidth = useMemo(() => {
    const metric2KeyList = bars?.map(item => item?.metric2?.metricKey) || [];
    const metric2Data = metricCardData?.filter((item: { metrics_name: string; }) => metric2KeyList?.includes(item?.metrics_name)) || [];
    const maxValue = Math.max(...metric2Data?.map((item: { value: any; }) => item?.value) || []);
    const maxKey = metric2Data?.find((item: { value: number; }) => Number(item?.value) === maxValue)?.metrics_name;
    const maxKeyConfig = componentMetrics?.find(item => item?.metricKey === maxKey);
    if (maxKeyConfig?.metricKey) {
      const metric2FormattedValue = formatMetricValue(maxKeyConfig, maxValue || '', data);
      const maxValueLength = (maxKeyConfig?.preUnit?.length || 0) + (metric2FormattedValue?.toString()?.length || 0) + (maxKeyConfig?.endUnit?.length || 0);
      return maxValueLength * 7;
    }
    return 60;
  }, [bars, data, componentMetrics, metricCardData]);

  return (
    <div className={classNames('dbd-metric-bar-card', className)} style={style}>
      {/* 标题和时间 */}
      <div className="dbd-metric-bar-card__header">
        <div className="dbd-metric-bar-card__title">
          {icon && <SvgIcon className='dbd-metric-bar-card__title-icon' type={icon} />}
          <div className='dbd-metric-bar-card__title-name'>{title || getName(isEn, titleEn, titleCn)}</div>
        </div>
        {formattedTime && (
          <div className="dbd-metric-bar-card__time">{formattedTime}</div>
        )}
      </div>
      {/* 进度条区域 */}
      <div className="dbd-metric-bar-card__bars">
        {bars?.map((item, index) => (
          <CardProgressBar
            key={index}
            data={data}
            item={item}
            index={index}
            minWidth={minWidth}
            defaultColor={defaultBarColors[index % defaultBarColors.length]}
            metricCardData={metricCardData}
            componentMetrics={componentMetrics}
          />
        ))}
      </div>
      {/* 底部卡片区域 - 通过 children 插槽传入 */}
      {children && (
        <div className="dbd-metric-bar-card__bottom-cards">
          {React.Children.map(children, (child: any) => {
            if (!child) return null;
            // 如果是有效的 React 元素，将 MetricBarCard 的数据能力透传给子组件（BottomCard 等）
            if (React.isValidElement(child)) {
              const element = child as React.ReactElement<any>;
              return React.cloneElement(element, {
                ...(element.props || {}),
                metricCardData,
                componentMetrics,
              } as any);
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
};

export default MetricBarCard;